import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:3041';
const REFRESH = 15000;

const TYPE_LABELS = {
  HIGH_TEMP:'Temp. e lartë', LOW_TEMP:'Temp. e ulët',
  HIGH_HUMIDITY:'Lag. e lartë', LOW_HUMIDITY:'Lag. e ulët',
  SENSOR_OFFLINE:'Sensor offline',
};
const SEV_CLS = {
  WARNING:'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  ALARM:'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  CRITICAL:'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};
const cardCls = "bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700";
const inputCls = "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

function fmtDT(ts) {
  if (!ts) return '–';
  return new Date(ts).toLocaleString('sq-AL', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' });
}
function SevBadge({ s }) {
  return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${SEV_CLS[s]||'bg-gray-100 text-gray-700'}`}>{s}</span>;
}
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2-lat1)*Math.PI/180, dLng=(lng2-lng1)*Math.PI/180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
  return R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
}
function fmtDist(m) {
  if (m==null) return '–';
  return m<1000?`${Math.round(m)}m`:`${(m/1000).toFixed(2)}km`;
}

/* ── Type icon for mobile cards ── */
function TypeIcon({ type, severity }) {
  const sevColor = severity === 'CRITICAL'
    ? 'text-red-500'
    : severity === 'ALARM'
    ? 'text-orange-500'
    : 'text-amber-500';

  if (type === 'SENSOR_OFFLINE') {
    return (
      <svg className={`w-6 h-6 ${sevColor}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <line x1="1" y1="1" x2="23" y2="23"/>
        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/>
        <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/>
        <path d="M10.71 5.05A16 16 0 0 1 22.56 9"/>
        <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/>
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
        <circle cx="12" cy="20" r="1"/>
      </svg>
    );
  }
  if (type?.includes('HUMIDITY')) {
    return (
      <svg className={`w-6 h-6 ${sevColor}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
      </svg>
    );
  }
  // default: thermometer (temp alarms or unknown)
  return (
    <svg className={`w-6 h-6 ${sevColor}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/>
    </svg>
  );
}

export default function Alarms() {
  const { user } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState(() => new URLSearchParams(location.search).get('tab') || 'alarms');
  const [alarms, setAlarms] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  // Inline resolve form
  const [resolveId, setResolveId] = useState(null);
  const [resolveAllOpen, setResolveAllOpen] = useState(false);
  const [resComment, setResComment] = useState('');
  const [resFiles, setResFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  // GPS
  const [gpsState, setGpsState] = useState('idle');
  const [gpsCoords, setGpsCoords] = useState(null);
  const [gpsDistance, setGpsDistance] = useState(null);
  const [gpsWithin, setGpsWithin] = useState(null);
  const [gpsPerimeter, setGpsPerimeter] = useState(100);

  // Filtrat e alarmeve (3 copë — pa kerko sensor)
  const [fStatus, setFStatus]   = useState('active');
  const [fSev, setFSev]         = useState('');
  const [fCompany, setFCompany] = useState('');

  // Mobile card expand state
  const [expandedAlarmId, setExpandedAlarmId] = useState(null);

  // Inspector report tab
  const [iRegion, setIRegion] = useState('');
  const [iDateFrom, setIDateFrom] = useState(new Date(Date.now()-7*86400000).toISOString().slice(0,16));
  const [iDateTo, setIDateTo] = useState(new Date().toISOString().slice(0,16));
  const [iData, setIData] = useState([]);
  const [iInspData, setIInspData] = useState([]);
  const [iLoading, setILoading] = useState(false);

  // Routine check-in tab
  const [chkCompany, setChkCompany] = useState('');
  const [chkComment, setChkComment] = useState('');
  const [chkFile, setChkFile] = useState(null);
  const [chkGpsState, setChkGpsState] = useState('idle');
  const [chkCoords, setChkCoords] = useState(null);
  const [chkSubmitting, setChkSubmitting] = useState(false);
  const [myCheckins, setMyCheckins] = useState([]);
  const chkFileRef = useRef(null);

  const auth = { Authorization:`Bearer ${localStorage.getItem('access_token')}` };
  const canSeeInspectors = ['SUPERADMIN','AVUK','KRYEINSPEKTORI'].includes(user?.role);
  const isInspector = user?.role === 'INSPEKTORI' || user?.role === 'KRYEINSPEKTORI';
  const canResolve = ['INSPEKTORI','KRYEINSPEKTORI','SUPERADMIN'].includes(user?.role);

  const fetchAlarms = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/alarms`, { headers: auth });
      setAlarms(r.data || []);
    } catch {}
  }, []);

  const fetchMyCheckins = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/inspections`, { headers: auth });
      setMyCheckins(r.data?.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        fetchAlarms(),
        axios.get(`${BACKEND_URL}/api/companies`,{headers:auth}).then(r=>setCompanies(r.data||[])).catch(()=>{}),
        axios.get(`${BACKEND_URL}/api/sensors`,  {headers:auth}).then(r=>setSensors(r.data||[])).catch(()=>{}),
        axios.get(`${BACKEND_URL}/api/regions`,  {headers:auth}).then(r=>setRegions(r.data||[])).catch(()=>{}),
        fetchMyCheckins(),
      ]);
      setLoading(false);
    };
    init();
    timerRef.current = setInterval(fetchAlarms, REFRESH);
    return () => clearInterval(timerRef.current);
  }, []);

  /* ── GPS ── */
  const acquireGPS = (onSuccess, onFail) => {
    if (!navigator.geolocation) { onFail?.('no-geo'); return; }
    navigator.geolocation.getCurrentPosition(
      pos => onSuccess(pos.coords.latitude, pos.coords.longitude),
      err => onFail?.(err.code===1?'denied':'error'),
      { enableHighAccuracy:true, timeout:15000, maximumAge:0 }
    );
  };

  const openResolve = (alarm) => {
    setResolveId(alarm.id);
    setResComment(''); setResFiles([]);
    setGpsState('loading'); setGpsCoords(null); setGpsDistance(null); setGpsWithin(null);
    const sensor  = sensors.find(s=>s.id===alarm.sensor_id);
    const company = companies.find(c=>String(c.id)===String(alarm.company_id));
    const tLat = sensor?.latitude  ?? company?.latitude  ?? null;
    const tLng = sensor?.longitude ?? company?.longitude ?? null;
    const perim = company?.perimeter_m ?? 100;
    setGpsPerimeter(perim);
    acquireGPS(
      (lat, lng) => {
        setGpsCoords({lat,lng}); setGpsState('ok');
        if (tLat!=null && tLng!=null) {
          const d = haversine(lat,lng,tLat,tLng);
          setGpsDistance(d); setGpsWithin(d<=perim);
          if (d>perim) toast.warning(`Jeni ${Math.round(d)}m larg — jashtë perimetrit ${Math.round(perim)}m`,{duration:5000});
        }
      },
      (err) => {
        setGpsState(err==='denied'?'denied':'error');
        toast.error(err==='denied'?'GPS i refuzuar':'Gabim GPS');
      }
    );
    setTimeout(()=>document.getElementById('res-comment')?.focus(),400);
  };

  const cancelResolve = () => {
    setResolveId(null); setResComment(''); setResFiles([]);
    setGpsState('idle'); setGpsCoords(null); setGpsDistance(null); setGpsWithin(null);
  };

  const submitResolve = async (alarmId) => {
    if (resFiles.length===0) { toast.error('Ngarkimi i fotove është i detyrueshëm'); fileRef.current?.click(); return; }
    if (gpsState==='loading') { toast.error('Prisni GPS...'); return; }
    setSubmitting(true);
    try {
      for (const f of resFiles) {
        const fd=new FormData(); fd.append('file',f);
        await axios.post(`${BACKEND_URL}/api/alarms/${alarmId}/attachments`,fd,{headers:{...auth,'Content-Type':'multipart/form-data'}});
      }
      const p=new URLSearchParams();
      if (resComment)           p.set('comment',resComment);
      if (gpsCoords?.lat!=null) p.set('inspector_lat',gpsCoords.lat);
      if (gpsCoords?.lng!=null) p.set('inspector_lng',gpsCoords.lng);
      if (gpsDistance!=null)    p.set('distance_m',gpsDistance.toFixed(1));
      if (gpsWithin!=null)      p.set('within_perimeter',gpsWithin);
      await axios.put(`${BACKEND_URL}/api/alarms/${alarmId}/resolve?${p}`,{},{headers:auth});
      toast.success('Alarmi u zgjidh ✅');
      cancelResolve(); await fetchAlarms();
    } catch { toast.error('Gabim gjatë zgjidhjes'); }
    finally { setSubmitting(false); }
  };

  /* ── Resolve all ── */
  const openResolveAll = () => {
    if (active.length === 0) return;
    setResolveId(null);
    setResolveAllOpen(true);
    setResComment(''); setResFiles([]);
    setGpsState('loading'); setGpsCoords(null); setGpsDistance(null); setGpsWithin(null);
    const firstAlarm = active[0];
    const company = companies.find(c => String(c.id) === String(firstAlarm?.company_id));
    setGpsPerimeter(company?.perimeter_m ?? 100);
    acquireGPS(
      (lat, lng) => { setGpsCoords({lat, lng}); setGpsState('ok'); },
      (err) => { setGpsState(err === 'denied' ? 'denied' : 'error'); toast.error(err === 'denied' ? 'GPS i refuzuar' : 'Gabim GPS'); }
    );
  };

  const cancelResolveAll = () => {
    setResolveAllOpen(false);
    setResComment(''); setResFiles([]);
    setGpsState('idle'); setGpsCoords(null); setGpsDistance(null); setGpsWithin(null);
  };

  const submitResolveAll = async () => {
    if (resFiles.length === 0) { toast.error('Ngarkimi i fotove është i detyrueshëm'); fileRef.current?.click(); return; }
    if (gpsState === 'loading') { toast.error('Prisni GPS...'); return; }
    setSubmitting(true);
    try {
      const p = new URLSearchParams();
      if (resComment)           p.set('comment', resComment);
      if (gpsCoords?.lat!=null) p.set('inspector_lat', gpsCoords.lat);
      if (gpsCoords?.lng!=null) p.set('inspector_lng', gpsCoords.lng);
      if (gpsDistance!=null)    p.set('distance_m', gpsDistance.toFixed(1));
      if (gpsWithin!=null)      p.set('within_perimeter', gpsWithin);
      for (const alarm of active) {
        for (const f of resFiles) {
          const fd = new FormData(); fd.append('file', f);
          await axios.post(`${BACKEND_URL}/api/alarms/${alarm.id}/attachments`, fd, {headers:{...auth,'Content-Type':'multipart/form-data'}});
        }
        await axios.put(`${BACKEND_URL}/api/alarms/${alarm.id}/resolve?${p}`, {}, {headers: auth});
      }
      toast.success(`${active.length} alarme u zgjidhën ✅`);
      cancelResolveAll(); await fetchAlarms();
    } catch { toast.error('Gabim gjatë zgjidhjes'); }
    finally { setSubmitting(false); }
  };

  /* ── Inspector report ── */
  const fetchInspReport = async () => {
    setILoading(true);
    try {
      const p=new URLSearchParams();
      if (iRegion)   p.set('region_id',iRegion);
      if (iDateFrom) p.set('date_from',iDateFrom);
      if (iDateTo)   p.set('date_to',iDateTo);
      const [r1,r2] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reports/inspector-activity?${p}`,{headers:auth}),
        axios.get(`${BACKEND_URL}/api/inspections?${p}`,{headers:auth}),
      ]);
      setIData(r1.data?.data||[]);
      setIInspData(r2.data?.data||[]);
    } catch { toast.error('Gabim gjatë ngarkimit'); }
    finally { setILoading(false); }
  };
  useEffect(()=>{ if(tab==='inspector') fetchInspReport(); },[tab]);

  /* ── Routine check-in GPS ── */
  const startChkGps = () => {
    setChkGpsState('loading'); setChkCoords(null);
    acquireGPS(
      (lat,lng)=>{ setChkCoords({lat,lng}); setChkGpsState('ok'); toast.success('GPS u mor'); },
      (err)=>{ setChkGpsState(err==='denied'?'denied':'error'); toast.error('Gabim GPS'); }
    );
  };

  const submitCheckin = async () => {
    if (!chkCompany) { toast.error('Zgjidhni kompaninë'); return; }
    if (!chkFile)    { toast.error('Foto është e detyrueshme'); chkFileRef.current?.click(); return; }
    setChkSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('company_id', chkCompany);
      fd.append('comment', chkComment);
      if (chkCoords) { fd.append('inspector_lat', chkCoords.lat); fd.append('inspector_lng', chkCoords.lng); }
      fd.append('file', chkFile);
      await axios.post(`${BACKEND_URL}/api/inspections`, fd, {
        headers:{...auth,'Content-Type':'multipart/form-data'}
      });
      toast.success('Kontrolli rutinor u regjistrua ✅');
      setChkCompany(''); setChkComment(''); setChkFile(null); setChkCoords(null); setChkGpsState('idle');
      await fetchMyCheckins();
    } catch { toast.error('Gabim gjatë regjistrimit'); }
    finally { setChkSubmitting(false); }
  };

  /* ── Computed ── */
  const active   = alarms.filter(a=>!a.resolved);
  const warnCnt  = active.filter(a=>a.severity==='WARNING').length;
  const alarmCnt = active.filter(a=>a.severity==='ALARM').length;
  const critCnt  = active.filter(a=>a.severity==='CRITICAL').length;

  const filtered = alarms.filter(a => {
    if (fStatus==='active'   && a.resolved)  return false;
    if (fStatus==='resolved' && !a.resolved) return false;
    if (fSev     && a.severity !== fSev)      return false;
    if (fCompany && String(a.company_id) !== String(fCompany)) return false;
    return true;
  });

  /* ── GPS Badge ── */
  const GpsBadge = ({ state, coords, distance, within, perimeter }) => {
    if (state==='loading') return <span className="text-xs text-amber-600 flex items-center gap-1 animate-pulse">📍 Duke marrë vendndodhjen...</span>;
    if (state==='denied')  return <span className="text-xs text-red-600 flex items-center gap-1">🚫 GPS i bllokuar — lejoni aksesin</span>;
    if (state==='error')   return <span className="text-xs text-red-600 flex items-center gap-1">❌ Gabim GPS</span>;
    if (state==='ok') {
      if (distance==null) return <span className="text-xs text-green-600">✅ GPS: {coords?.lat?.toFixed(5)}, {coords?.lng?.toFixed(5)}</span>;
      return (
        <span className={`text-xs flex items-center gap-2 font-medium ${within?'text-green-600':'text-red-600'}`}>
          {within?'✅ Brenda perimetrit':'⚠️ Jashtë perimetrit'}
          <span className="font-mono bg-white dark:bg-gray-800 border border-current/20 px-2 py-0.5 rounded text-xs">
            {fmtDist(distance)} / {Math.round(perimeter)}m
          </span>
        </span>
      );
    }
    return <span className="text-xs text-gray-400">📍 GPS në pritje...</span>;
  };

  /* ── Inline resolve form (shared between mobile and desktop) ── */
  const ResolveForm = ({ alarm }) => (
    <div className="rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-4 mt-1 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-blue-700 dark:text-blue-300">
          ✅ Zgjidh — <span className="font-normal text-gray-700 dark:text-gray-300">{alarm.sensor_name} · {alarm.company_name}</span>
        </h3>
        <button onClick={cancelResolve} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
      </div>

      {/* GPS */}
      <div className={`flex items-center gap-3 rounded-lg px-3 py-2.5 border ${
        gpsState==='ok'&&gpsWithin===true ?'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700':
        gpsState==='ok'&&gpsWithin===false?'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-700':
        gpsState==='ok'?'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700':
        (gpsState==='denied'||gpsState==='error')?'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-700':
        'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700'
      }`}>
        <GpsBadge state={gpsState} coords={gpsCoords} distance={gpsDistance} within={gpsWithin} perimeter={gpsPerimeter}/>
        {(gpsState==='denied'||gpsState==='error')&&(
          <button onClick={()=>openResolve(alarm)} className="ml-auto text-xs px-2 py-1 bg-blue-600 text-white rounded-lg">Provo</button>
        )}
      </div>

      <div className="text-xs text-gray-500 flex items-center gap-2">
        🕐 <span className="font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-200 dark:border-gray-600">
          {new Date().toLocaleString('sq-AL',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit',second:'2-digit'})}
        </span>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
          💬 Komenti <span className="font-normal text-gray-400">(opsional)</span>
        </label>
        <textarea id="res-comment" value={resComment} onChange={e=>setResComment(e.target.value)} rows={3}
          placeholder="Veprimi i ndërmarrë, arsyeja, gjendja e pajisjes..."
          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"/>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
          📸 Foto / Dokument <span className="text-red-500 font-semibold">* E detyrueshme</span>
        </label>
        <div onClick={()=>fileRef.current?.click()}
          className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed cursor-pointer transition ${
            resFiles.length>0?'border-green-400 bg-green-50 dark:bg-green-900/20':'border-gray-300 dark:border-gray-600 hover:border-blue-400 bg-white dark:bg-gray-700/30'
          }`}>
          {resFiles.length===0?(<>
            <span className="text-3xl">📎</span>
            <span className="text-sm text-gray-500">Klikoni për të zgjedhur foto ose dokument</span>
          </>):(
            <div className="flex flex-wrap gap-2 w-full justify-center">
              {resFiles.map((f,i)=>(
                <div key={i} className="flex items-center gap-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300">
                  {f.type.startsWith('image')?'🖼️':'📄'} {f.name}
                  <button onClick={e=>{e.stopPropagation();setResFiles(p=>p.filter((_,j)=>j!==i))}} className="text-gray-400 hover:text-red-500">&times;</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.doc,.docx" className="hidden"
          onChange={e=>setResFiles(p=>[...p,...Array.from(e.target.files||[])])}/>
      </div>

      <div className="pt-2 border-t border-blue-100 dark:border-blue-800 space-y-2">
        {resFiles.length===0&&(
          <p className="text-xs text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1">
            <span>⚠️</span><span>Ngarko të paktën 1 foto për të konfirmuar</span>
          </p>
        )}
        <div className="flex gap-2">
          <button onClick={()=>submitResolve(alarm.id)} disabled={submitting||gpsState==='loading'}
            className={`flex-1 py-2.5 text-white text-sm font-semibold rounded-xl flex items-center justify-center gap-1.5 transition ${
              resFiles.length>0&&gpsState!=='loading'?'bg-green-600 hover:bg-green-700':'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
            } disabled:opacity-60`}>
            {submitting?'Duke ruajtur...':'✅ Konfirmo'}
          </button>
          <button onClick={cancelResolve} className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl transition">
            Anulo
          </button>
        </div>
      </div>
    </div>
  );

  const TABS = [
    { key:'alarms',    label:'🚨 Alarmet' },
    ...(isInspector ? [{ key:'checkin', label:'📋 Kontrolli Rutinor' }] : []),
    ...(canSeeInspectors ? [{ key:'inspector', label:'🗂️ Raportet' }] : []),
  ];

  return (
    <Layout>
      <div className="p-4 space-y-4">

        {/* Header + Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-4 flex-wrap">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Menaxhimi i Alarmeve</h1>
            {TABS.length > 1 && (
              <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-xs font-medium">
                {TABS.map(({key,label})=>(
                  <button key={key} onClick={()=>setTab(key)}
                    className={`px-3 py-1.5 transition ${tab===key?'bg-blue-600 text-white':'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {tab==='alarms' && ['SUPERADMIN','AVUK','KRYEINSPEKTORI'].includes(user?.role) && (
            <button onClick={openResolveAll} disabled={active.length===0}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition">
              Zgjidh të gjithë ({active.length})
            </button>
          )}
        </div>

        {/* ── Resolve All Form ── */}
        {resolveAllOpen && (
          <div className="rounded-xl border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-red-700 dark:text-red-300">
                ✅ Zgjidh të gjithë — <span className="font-normal text-gray-700 dark:text-gray-300">{active.length} alarme aktive</span>
              </h3>
              <button onClick={cancelResolveAll} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className={`flex items-center gap-3 rounded-lg px-3 py-2.5 border ${
              gpsState==='ok'?'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700':
              (gpsState==='denied'||gpsState==='error')?'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-700':
              'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700'
            }`}>
              <GpsBadge state={gpsState} coords={gpsCoords} distance={gpsDistance} within={gpsWithin} perimeter={gpsPerimeter}/>
              {(gpsState==='denied'||gpsState==='error')&&(
                <button onClick={openResolveAll} className="ml-auto text-xs px-2 py-1 bg-blue-600 text-white rounded-lg">Provo</button>
              )}
            </div>
            <div className="text-xs text-gray-500 flex items-center gap-2">
              🕐 <span className="font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-200 dark:border-gray-600">
                {new Date().toLocaleString('sq-AL',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit',second:'2-digit'})}
              </span>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">💬 Komenti <span className="font-normal text-gray-400">(opsional)</span></label>
              <textarea value={resComment} onChange={e=>setResComment(e.target.value)} rows={3}
                placeholder="Veprimi i ndërmarrë, arsyeja, gjendja e pajisjes..."
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">📸 Foto / Dokument <span className="text-red-500 font-semibold">* E detyrueshme</span></label>
              <div onClick={()=>fileRef.current?.click()}
                className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed cursor-pointer transition ${
                  resFiles.length>0?'border-green-400 bg-green-50 dark:bg-green-900/20':'border-gray-300 dark:border-gray-600 hover:border-blue-400 bg-white dark:bg-gray-700/30'
                }`}>
                {resFiles.length===0?(<><span className="text-3xl">📎</span><span className="text-sm text-gray-500">Klikoni për të zgjedhur foto ose dokument</span></>):(
                  <div className="flex flex-wrap gap-2 w-full justify-center">
                    {resFiles.map((f,i)=>(
                      <div key={i} className="flex items-center gap-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300">
                        {f.type.startsWith('image')?'🖼️':'📄'} {f.name}
                        <button onClick={e=>{e.stopPropagation();setResFiles(p=>p.filter((_,j)=>j!==i))}} className="text-gray-400 hover:text-red-500">&times;</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {resFiles.length===0&&<p className="text-xs text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1"><span>⚠️</span><span>Ngarko të paktën 1 foto për të konfirmuar</span></p>}
            <div className="flex gap-2 pt-2 border-t border-red-100 dark:border-red-800">
              <button onClick={submitResolveAll} disabled={submitting||gpsState==='loading'}
                className={`flex-1 py-2.5 text-white text-sm font-semibold rounded-xl flex items-center justify-center gap-1.5 transition ${
                  resFiles.length>0&&gpsState!=='loading'?'bg-green-600 hover:bg-green-700':'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                } disabled:opacity-60`}>
                {submitting?'Duke ruajtur...':'✅ Konfirmo të gjithë'}
              </button>
              <button onClick={cancelResolveAll} className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl transition">Anulo</button>
            </div>
          </div>
        )}

        {/* ════════ TAB: ALARMET ════════ */}
        {tab==='alarms' && (<>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              {label:'Aktive totale',val:active.length, color:'text-gray-900 dark:text-white'},
              {label:'WARNING',      val:warnCnt,        color:'text-amber-600 dark:text-amber-400'},
              {label:'ALARM',        val:alarmCnt,       color:'text-orange-600 dark:text-orange-400'},
              {label:'CRITICAL',     val:critCnt,        color:'text-red-600 dark:text-red-400'},
            ].map(s=>(
              <div key={s.label} className={`${cardCls} p-4 text-center`}>
                <p className={`text-2xl font-bold ${s.color}`}>{s.val}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          {/* 3 filtrat */}
          <div className={`${cardCls} p-3`}>
            <div className="grid grid-cols-3 gap-2">
              <select value={fStatus} onChange={e=>setFStatus(e.target.value)} className={inputCls}>
                <option value="active">Aktive</option>
                <option value="resolved">Të zgjidhura</option>
                <option value="all">Të gjitha</option>
              </select>
              <select value={fSev} onChange={e=>setFSev(e.target.value)} className={inputCls}>
                <option value="">Të gjitha severitetet</option>
                <option value="WARNING">WARNING</option>
                <option value="ALARM">ALARM</option>
                <option value="CRITICAL">CRITICAL</option>
              </select>
              <select value={fCompany} onChange={e=>setFCompany(e.target.value)} className={inputCls}>
                <option value="">Të gjitha kompanitë</option>
                {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          {loading ? (
            <div className={`${cardCls} p-8 text-center text-gray-500 text-sm`}>Duke ngarkuar...</div>
          ) : filtered.length === 0 ? (
            <div className={`${cardCls} p-8 text-center text-gray-500 text-sm`}>Asnjë alarm i gjetur</div>
          ) : (<>

            {/* ── MOBILE card list (hidden on md+) ── */}
            <div className="md:hidden space-y-2">
              {filtered.map(alarm => {
                const isExpanded = expandedAlarmId === alarm.id;
                const isResolving = resolveId === alarm.id;
                const valueStr = alarm.value != null
                  ? `${alarm.value}${alarm.alarm_type?.includes('TEMP') ? '°C' : '%'}`
                  : '–';
                const thresholdStr = alarm.threshold != null
                  ? `${alarm.threshold}${alarm.alarm_type?.includes('TEMP') ? '°C' : '%'}`
                  : '–';

                return (
                  <div key={alarm.id} className={`${cardCls} overflow-hidden ${alarm.resolved ? 'opacity-70' : ''} ${isResolving ? 'ring-2 ring-blue-400' : ''}`}>
                    {/* Card main row */}
                    <div className="flex items-center gap-3 px-3 py-3">
                      {/* Left: type icon */}
                      <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-gray-50 dark:bg-gray-700">
                        <TypeIcon type={alarm.alarm_type} severity={alarm.severity} />
                      </div>

                      {/* Center: label + company + time */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight truncate">
                          {TYPE_LABELS[alarm.alarm_type] || alarm.alarm_type || '–'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{alarm.company_name || '–'}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">{fmtDT(alarm.ts)}</p>
                      </div>

                      {/* Right: badge + ellipsis button */}
                      <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                        <SevBadge s={alarm.severity} />
                        <button
                          onClick={() => setExpandedAlarmId(isExpanded ? null : alarm.id)}
                          className="p-1 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                          aria-label="Shfaq detajet"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <circle cx="5" cy="12" r="1.5"/>
                            <circle cx="12" cy="12" r="1.5"/>
                            <circle cx="19" cy="12" r="1.5"/>
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Expanded panel */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 dark:border-gray-700 px-3 py-3 space-y-2 bg-gray-50 dark:bg-gray-700/30">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500 dark:text-gray-400">Sensori</span>
                          <span className="font-medium text-gray-800 dark:text-gray-200">{alarm.sensor_name || '–'}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500 dark:text-gray-400">Vlera / Pragu</span>
                          <span className="font-medium text-gray-800 dark:text-gray-200">{valueStr} / {thresholdStr}</span>
                        </div>
                        {alarm.resolved ? (
                          <div className="flex items-center gap-2 pt-1">
                            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded font-medium">
                              ✅ Zgjidhur
                            </span>
                            {alarm.within_perimeter != null && (
                              <span className={`text-xs ${alarm.within_perimeter ? 'text-green-600' : 'text-red-500'}`}>
                                {alarm.within_perimeter ? '✅ Në vend' : '⚠️ Larg'}
                                {alarm.distance_m != null && <span className="text-gray-400"> ({fmtDist(alarm.distance_m)})</span>}
                              </span>
                            )}
                          </div>
                        ) : (
                          canResolve && (
                            <button
                              onClick={() => { openResolve(alarm); setExpandedAlarmId(null); }}
                              className="w-full mt-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition"
                            >
                              Zgjidh Alarmin
                            </button>
                          )
                        )}
                      </div>
                    )}

                    {/* Inline resolve form for mobile */}
                    {isResolving && (
                      <div className="border-t border-blue-200 dark:border-blue-700 px-3 pb-3">
                        <ResolveForm alarm={alarm} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── DESKTOP table (hidden on mobile) ── */}
            <div className={`${cardCls} overflow-hidden hidden md:block`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      {['Koha','Sensori','Kompania','Tipi','Serioziteti','Vlera','Pragu','Statusi','Veprimet'].map(h=>(
                        <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {filtered.map(alarm=>(<>
                      <tr key={alarm.id}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition ${alarm.resolved?'opacity-60':''} ${resolveId===alarm.id?'bg-blue-50 dark:bg-blue-900/10':''}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-gray-400">{fmtDT(alarm.ts)}</td>
                        <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900 dark:text-white">{alarm.sensor_name||'–'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-gray-400">{alarm.company_name||'–'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{TYPE_LABELS[alarm.alarm_type]||alarm.alarm_type||'–'}</td>
                        <td className="px-3 py-2 whitespace-nowrap"><SevBadge s={alarm.severity}/></td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">
                          {alarm.value!=null?`${alarm.value}${alarm.alarm_type?.includes('TEMP')?'°C':'%'}`:'–'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-gray-400">
                          {alarm.threshold!=null?`${alarm.threshold}${alarm.alarm_type?.includes('TEMP')?'°C':'%'}`:'–'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {alarm.resolved
                            ? <div className="space-y-0.5">
                                <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded font-medium block w-fit">Zgjidhur</span>
                                {alarm.within_perimeter!=null&&(
                                  <span className={`text-xs block ${alarm.within_perimeter?'text-green-600':'text-red-500'}`}>
                                    {alarm.within_perimeter?'✅ Në vend':'⚠️ Larg'}
                                    {alarm.distance_m!=null&&<span className="text-gray-400"> ({fmtDist(alarm.distance_m)})</span>}
                                  </span>
                                )}
                              </div>
                            : <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded font-medium">Aktiv</span>
                          }
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap min-w-[140px]">
                          {!alarm.resolved && resolveId!==alarm.id && (
                            <button onClick={()=>openResolve(alarm)}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition">
                              Zgjidh
                            </button>
                          )}
                          {alarm.resolved && (
                            <div className="text-xs text-gray-400 space-y-0.5">
                              <div>{fmtDT(alarm.resolved_at)}</div>
                              {alarm.resolved_by_name&&<div className="text-blue-500 font-medium">{alarm.resolved_by_name}</div>}
                              {alarm.resolved_comment&&<div className="italic truncate max-w-[120px]">"{alarm.resolved_comment}"</div>}
                              {(alarm.resolved_files||[]).length>0&&(
                                <div className="flex gap-1 flex-wrap">
                                  {alarm.resolved_files.map((u,i)=>(
                                    <a key={i} href={`${BACKEND_URL}${u}`} target="_blank" rel="noreferrer"
                                      className="text-blue-500 hover:underline">📎{i+1}</a>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>

                      {/* Inline resolve form */}
                      {resolveId===alarm.id&&(
                        <tr key={`rf-${alarm.id}`}>
                          <td colSpan={9} className="px-2 py-0 pb-3">
                            <ResolveForm alarm={alarm} />
                          </td>
                        </tr>
                      )}
                    </>))}
                  </tbody>
                </table>
              </div>
            </div>

          </>)}

          <p className="text-xs text-gray-400">Freskim automatik çdo {REFRESH/1000}s · {filtered.length}/{alarms.length} rekorde</p>
        </>)}

        {/* ════════ TAB: KONTROLLI RUTINOR ════════ */}
        {tab==='checkin' && (<>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Form i ri */}
            <div className={`${cardCls} p-5 space-y-4`}>
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                📋 Kontroll i Ri Rutinor
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Regjistroni vizitën tuaj rutinore te kompania. Kërkohet foto si dëshmi e pranisë.
              </p>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">🏢 Kompania *</label>
                <select value={chkCompany} onChange={e=>setChkCompany(e.target.value)} className={inputCls}>
                  <option value="">-- Zgjidhni kompaninë --</option>
                  {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              {/* GPS */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">📍 Vendndodhja</label>
                <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${
                  chkGpsState==='ok'?'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700':
                  chkGpsState==='loading'?'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700':
                  (chkGpsState==='denied'||chkGpsState==='error')?'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-700':
                  'bg-gray-50 border-gray-200 dark:bg-gray-700/30 dark:border-gray-600'
                }`}>
                  {chkGpsState==='idle'&&<span className="text-xs text-gray-400">GPS nuk është marrë ende (opsional)</span>}
                  {chkGpsState==='loading'&&<span className="text-xs text-amber-600 animate-pulse">📍 Duke marrë GPS...</span>}
                  {chkGpsState==='ok'&&<span className="text-xs text-green-600">✅ {chkCoords?.lat?.toFixed(5)}, {chkCoords?.lng?.toFixed(5)}</span>}
                  {chkGpsState==='denied'&&<span className="text-xs text-red-600">🚫 GPS i bllokuar</span>}
                  {chkGpsState==='error'&&<span className="text-xs text-red-600">❌ Gabim GPS</span>}
                  <button onClick={startChkGps} disabled={chkGpsState==='loading'}
                    className="ml-auto text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition whitespace-nowrap">
                    {chkGpsState==='ok'?'🔄 Rimerr':'📍 Merr GPS'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">💬 Observatat / Komenti</label>
                <textarea value={chkComment} onChange={e=>setChkComment(e.target.value)} rows={3}
                  placeholder="Gjendja e pajisjes, temperatura, ndonjë problem i vërejtur..."
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"/>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  📸 Foto e kontrollit <span className="text-red-500 font-semibold">* E detyrueshme</span>
                </label>
                <div onClick={()=>chkFileRef.current?.click()}
                  className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed cursor-pointer transition ${
                    chkFile?'border-green-400 bg-green-50 dark:bg-green-900/20':'border-gray-300 dark:border-gray-600 hover:border-blue-400 bg-white dark:bg-gray-700/30'
                  }`}>
                  {!chkFile?(<>
                    <span className="text-3xl">📷</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Klikoni për të ngarkuar foton e kontrollit</span>
                    <span className="text-xs text-gray-400">JPG · PNG · max 10MB</span>
                  </>):(
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🖼️</span>
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{chkFile.name}</p>
                        <p className="text-xs text-gray-400">{(chkFile.size/1024).toFixed(0)} KB</p>
                      </div>
                      <button onClick={e=>{e.stopPropagation();setChkFile(null)}} className="ml-2 text-gray-400 hover:text-red-500 text-xl">&times;</button>
                    </div>
                  )}
                </div>
                <input ref={chkFileRef} type="file" accept="image/*" className="hidden"
                  onChange={e=>setChkFile(e.target.files?.[0]||null)}/>
              </div>

              <div className="pt-1 border-t border-gray-100 dark:border-gray-700 flex items-center gap-3">
                <button onClick={submitCheckin} disabled={chkSubmitting||!chkCompany||!chkFile}
                  className="px-5 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition">
                  {chkSubmitting?'Duke regjistruar...':'✅ Regjistro Kontrollin'}
                </button>
                <span className="text-xs text-gray-400">
                  {!chkCompany?'⚠️ Zgjidhni kompaninë':!chkFile?'⚠️ Ngarko foton':'Gati për regjistrim'}
                </span>
              </div>
            </div>

            {/* Historiku im */}
            <div className={`${cardCls} p-5`}>
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center justify-between">
                <span>🗓️ Kontrollet e Mia të Fundit</span>
                <button onClick={fetchMyCheckins} className="text-xs text-blue-500 hover:underline">🔄 Rifresko</button>
              </h2>
              {myCheckins.length===0
                ? <p className="text-sm text-gray-400 dark:text-gray-500 py-6 text-center">Asnjë kontroll i regjistruar ende</p>
                : <div className="space-y-3 max-h-[450px] overflow-y-auto pr-1">
                    {myCheckins.map((c,i)=>(
                      <div key={i} className="rounded-lg border border-gray-100 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-700/30">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{c.company_name||'–'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{fmtDT(c.ts)}</p>
                            {c.comment&&<p className="text-xs text-gray-600 dark:text-gray-400 mt-1 italic">"{c.comment}"</p>}
                            {c.inspector_lat&&<p className="text-xs text-gray-400 font-mono mt-0.5">📍 {c.inspector_lat?.toFixed(4)}, {c.inspector_lng?.toFixed(4)}</p>}
                          </div>
                          {c.file_url&&(
                            <a href={`${BACKEND_URL}${c.file_url}`} target="_blank" rel="noreferrer"
                              className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700">
                              <img src={`${BACKEND_URL}${c.file_url}`} alt="foto" className="w-full h-full object-cover"
                                onError={e=>{e.target.style.display='none';e.target.parentElement.innerHTML='📄';}}/>
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
              }
            </div>
          </div>
        </>)}

        {/* ════════ TAB: RAPORTET ════════ */}
        {tab==='inspector' && (<>
          <div className={`${cardCls} p-4`}>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <select value={iRegion} onChange={e=>setIRegion(e.target.value)} className={inputCls}>
                <option value="">Të gjitha rajonet</option>
                {regions.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Nga data</label>
                <input type="datetime-local" value={iDateFrom} onChange={e=>setIDateFrom(e.target.value)} className={inputCls}/>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Deri data</label>
                <input type="datetime-local" value={iDateTo} onChange={e=>setIDateTo(e.target.value)} className={inputCls}/>
              </div>
              <div className="flex items-end">
                <button onClick={fetchInspReport} disabled={iLoading}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition">
                  {iLoading?'Duke ngarkuar...':'🔍 Filtro'}
                </button>
              </div>
            </div>
          </div>

          {/* Alarmet e zgjidhura */}
          <div className={cardCls}>
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">⚡ Alarmet e Zgjidhura nga Inspektorët</h3>
              <span className="text-xs text-gray-400">{iData.length} rekorde</span>
            </div>
            <div className="flex items-center gap-5 px-4 py-2.5 text-xs text-gray-500 border-b border-gray-50 dark:border-gray-700 flex-wrap">
              <span>✅ <b className="text-green-600">{iData.filter(d=>d.within_perimeter===true).length}</b> në vend</span>
              <span>⚠️ <b className="text-red-500">{iData.filter(d=>d.within_perimeter===false).length}</b> larg vendit</span>
              <span>📎 <b className="text-gray-700 dark:text-gray-300">{iData.filter(d=>(d.resolved_files||[]).length>0).length}</b> me skedar</span>
            </div>
            {iData.length===0
              ? <div className="p-6 text-center text-sm text-gray-400">Asnjë aktivitet</div>
              : <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                      <tr>
                        {['Zgjidhur në','Inspektori','Sensori','Kompania','Tipi','Sev.','GPS','Komenti','Skedarët'].map(h=>(
                          <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {iData.map((row,i)=>(
                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-3 py-2.5 whitespace-nowrap text-gray-600 dark:text-gray-400">{fmtDT(row.resolved_at)}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap font-medium text-blue-600 dark:text-blue-400">{row.resolved_by_name||'–'}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap text-gray-900 dark:text-white">{row.sensor_name||'–'}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap text-gray-600 dark:text-gray-400">{row.company_name||'–'}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{TYPE_LABELS[row.alarm_type]||row.alarm_type||'–'}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap"><SevBadge s={row.severity}/></td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            {row.within_perimeter==null
                              ? <span className="text-gray-300 text-xs">–</span>
                              : <span className={`text-xs font-medium ${row.within_perimeter?'text-green-600':'text-red-500'}`}>
                                  {row.within_perimeter?'✅ Në vend':'⚠️ Larg'}
                                  {row.distance_m!=null&&<span className="text-gray-400 font-normal"> {fmtDist(row.distance_m)}</span>}
                                </span>
                            }
                          </td>
                          <td className="px-3 py-2.5 max-w-[180px]">
                            {row.resolved_comment?<span className="text-xs italic text-gray-600 dark:text-gray-400">"{row.resolved_comment}"</span>:<span className="text-gray-300 text-xs">—</span>}
                          </td>
                          <td className="px-3 py-2.5">
                            {(row.resolved_files||[]).length===0
                              ? <span className="text-gray-300 text-xs">—</span>
                              : <div className="flex gap-1">
                                  {row.resolved_files.map((u,fi)=>(
                                    <a key={fi} href={`${BACKEND_URL}${u}`} target="_blank" rel="noreferrer"
                                      className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-1 rounded border border-blue-100 dark:border-blue-800">
                                      {/\.(jpg|jpeg|png|gif)$/i.test(u)?'🖼️':'📄'}{fi+1}
                                    </a>
                                  ))}
                                </div>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
            }
          </div>

          {/* Kontrollet Rutinore */}
          <div className={cardCls}>
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">📋 Kontrollet Rutinore</h3>
              <span className="text-xs text-gray-400">{iInspData.length} rekorde</span>
            </div>
            {iInspData.length===0
              ? <div className="p-6 text-center text-sm text-gray-400">Asnjë kontroll rutinor</div>
              : <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                      <tr>
                        {['Data','Inspektori','Kompania','GPS','Observatat','Foto'].map(h=>(
                          <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {iInspData.map((row,i)=>(
                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-3 py-2.5 whitespace-nowrap text-gray-600 dark:text-gray-400">{fmtDT(row.ts)}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap font-medium text-blue-600 dark:text-blue-400">{row.inspector_name||'–'}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap text-gray-900 dark:text-white">{row.company_name||'–'}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap text-xs">
                            {row.inspector_lat
                              ? <span className="text-green-600 font-mono">📍 {row.inspector_lat?.toFixed(4)}, {row.inspector_lng?.toFixed(4)}</span>
                              : <span className="text-gray-300">–</span>
                            }
                          </td>
                          <td className="px-3 py-2.5 max-w-[200px]">
                            {row.comment?<span className="text-xs italic text-gray-600 dark:text-gray-400">"{row.comment}"</span>:<span className="text-gray-300 text-xs">—</span>}
                          </td>
                          <td className="px-3 py-2.5">
                            {row.file_url
                              ? <a href={`${BACKEND_URL}${row.file_url}`} target="_blank" rel="noreferrer"
                                  className="text-xs bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-2 py-1 rounded border border-green-100 dark:border-green-800">
                                  🖼️ Foto
                                </a>
                              : <span className="text-gray-300 text-xs">—</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
            }
          </div>
        </>)}
      </div>
    </Layout>
  );
}
